import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AlertMessage, ApiError } from './alert.interfaces';

@Injectable({
    providedIn: 'root',
})

/**
 * Serviço responsável por gerenciar alertas/mensagens do sistema
 *
 * Características:
 * - Gerencia mensagens de sucesso, erro e informação
 * - Mantém estado entre navegações (opcional)
 * - Tratamento inteligente de erros da API
 *
 * Uso básico:
 * constructor(private alertService: AlertService) {}
 *
 * // Mensagens simples
 * this.alertService.success('Operação realizada com sucesso');
 * this.alertService.info('Informação importante');
 *
 * // Tratamento de erros da API
 * this.alertService.error(error); // Extrai automaticamente a mensagem adequada
 *
 * // Manter mensagem após navegação
 * this.alertService.success('Mensagem', true);
 *
 * Tratamento de erros específicos:
 * - Status 0: "Servidor indisponível"
 * - Status 401: "Sessão expirada"
 * - Status 404: "Error 404"
 * - Status 500+: "Erro interno do servidor"
 * - Erros do DRF: Extrai mensagens de detail, message ou non_field_errors
 */
export class AlertService {
    private subject = new BehaviorSubject<AlertMessage | null>(null);
    private keepAfterRouteChange = false;

    constructor(private router: Router) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (this.keepAfterRouteChange) {
                    this.keepAfterRouteChange = false;
                } else {
                    this.clear();
                }
            }
        });
    }

    getAlert(): Observable<AlertMessage | null> {
        return this.subject.asObservable();
    }

    success(message: string, keepAfterRouteChange = false) {
        this.keepAfterRouteChange = keepAfterRouteChange;
        this.subject.next({ type: 'success', text: message });
    }

    error(messageOrError: string | ApiError, keepAfterRouteChange = false) {
        this.keepAfterRouteChange = keepAfterRouteChange;
        console.debug(typeof messageOrError);
        if (typeof messageOrError === 'string') {
            this.subject.next({ type: 'error', text: messageOrError });
            return;
        }

        // Teste seguro da extração da mensagem
        const errorMessage = this.testErrorExtraction(messageOrError);
        console.debug(
            'Mensagem extraída:',
            errorMessage,
            'Erro original:',
            messageOrError
        );

        this.subject.next({ type: 'error', text: errorMessage });
    }

    getErrorMessage(error: ApiError): string {
        return this.testErrorExtraction(error);
    }

    private extractErrorMessage(error: ApiError): string {
        // Verifica status HTTP específicos
        if (error.status === 0) {
            return 'Servidor indisponível. Por favor, tente novamente mais tarde.';
        }
        if (error.status === 401) {
            return 'Sessão expirada. Por favor, faça login novamente.';
        }
        if (error.status === 404) {
            return (
                error.error.message ||
                'Error 404. Por favor, informar à administração.'
            );
        }
        if (error.status >= 500) {
            return 'Erro interno do servidor. Por favor, tente novamente mais tarde.';
        }

        // Verifica outras mensagens do Django REST Framework
        if (error.error) {
            if (error.error?.detail) {
                return error.error.detail;
            }
            if (error.error.message) {
                return error.error.message;
            }
            if (error.error.non_field_errors?.length) {
                return error.error.non_field_errors[0];
            }
            // Verifica se há mensagens de erro em campos específicos
            const fieldErrors = Object.values(error.error).flat();
            if (fieldErrors.length) {
                return fieldErrors[0];
            }
        }

        // Fallbacks
        return (
            error.message || error.statusText || 'Ocorreu um erro inesperado.'
        );
    }

    info(message: string, keepAfterRouteChange = false) {
        this.keepAfterRouteChange = keepAfterRouteChange;
        this.subject.next({ type: 'info', text: message });
    }

    clear() {
        this.subject.next(null);
    }

    private testErrorExtraction(error: ApiError): string {
        try {
            return this.extractErrorMessage(error);
        } catch (e) {
            console.warn('Erro ao extrair mensagem:', e);
            // Fallback para o comportamento anterior
            return typeof error === 'string'
                ? error
                : 'Ocorreu um erro inesperado.';
        }
    }
}
